import type { WebmailSearchDto } from '@glory/contracts';

/** Всё, что раздел «Почта» хранит в адресе: открытая цепочка и условия поиска. */
export interface MailSearch {
  readonly thread?: string | undefined;
  /** Слова для поиска по тексту, теме, отправителю. */
  readonly q?: string | undefined;
  readonly from?: string | undefined;
  readonly to?: string | undefined;
  readonly subject?: string | undefined;
  readonly att?: true | undefined;
  readonly unread?: true | undefined;
  readonly flag?: true | undefined;
  readonly after?: string | undefined;
  readonly before?: string | undefined;
  /** Искать только в открытой папке, а не во всём ящике. */
  readonly here?: true | undefined;
  /** Переписка с этими адресами через запятую (вкладка «Письма» клиента): любой из них — отправитель или получатель. */
  readonly people?: string | undefined;
}

const text = (value: unknown): string | undefined =>
  typeof value === 'string' && value.trim() !== '' ? value.slice(0, 200) : undefined;
const flag = (value: unknown): true | undefined =>
  value === true || value === 'true' || value === 1 || value === '1' ? true : undefined;
const day = (value: unknown): string | undefined =>
  typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value) ? value : undefined;

/** Адреса из `?people=`: только похожие на адрес, без повторов, не больше 20 — остальное отбрасывается. */
export function peopleOf(value: unknown): string[] {
  if (typeof value !== 'string') return [];
  const addresses = value
    .split(',')
    .map((part) => part.trim())
    .filter((part) => part.length >= 3 && part.length <= 320 && part.includes('@'));
  return [...new Set(addresses)].slice(0, 20);
}

/** Проверка адреса раздела: лишнее отбрасывается, неверные значения не ломают страницу. */
export function validateMailSearch(search: Record<string, unknown>): MailSearch {
  return {
    thread: typeof search.thread === 'string' ? search.thread : undefined,
    q: text(search.q),
    from: text(search.from),
    to: text(search.to),
    subject: text(search.subject),
    att: flag(search.att),
    unread: flag(search.unread),
    flag: flag(search.flag),
    after: day(search.after),
    before: day(search.before),
    here: flag(search.here),
    people: peopleOf(search.people).join(',') || undefined,
  };
}

/** Есть ли что искать: без условий раздел показывает обычную папку. */
export function isSearching(search: MailSearch): boolean {
  return [
    search.q,
    search.from,
    search.to,
    search.subject,
    search.att,
    search.unread,
    search.flag,
    search.after,
    search.before,
    search.people,
  ].some((value) => value !== undefined);
}

/** Условия поиска для сервера; даты — начало и конец суток по местному времени (включительно). */
export function toSearchDto(search: MailSearch): WebmailSearchDto | undefined {
  if (!isSearching(search)) return undefined;
  return {
    ...(search.q ? { text: search.q } : {}),
    ...(search.from ? { from: search.from } : {}),
    ...(search.to ? { to: search.to } : {}),
    ...(search.subject ? { subject: search.subject } : {}),
    ...(search.att ? { hasAttachment: true } : {}),
    ...(search.unread ? { unread: true } : {}),
    ...(search.flag ? { flagged: true } : {}),
    ...(search.people ? { participants: peopleOf(search.people) } : {}),
    ...(search.after ? { after: new Date(`${search.after}T00:00:00`) } : {}),
    ...(search.before
      ? { before: new Date(new Date(`${search.before}T00:00:00`).getTime() + 86_400_000) }
      : {}),
  };
}

/** Адрес без условий поиска, но с открытой цепочкой — «сбросить поиск». */
export const withoutSearch = (search: MailSearch): MailSearch => ({ thread: search.thread });
